// Thin wrappers around UAVSimNet operator API. Returns the parsed body.
//
// Phase 6: adds `auth` and `users`. The pre-existing surface (callable
// `health`, `backend.describe`, `runs.submit`, `runs.artifact{Blob,Text,Json}`)
// is preserved verbatim so the rest of the SPA continues to compile.
import { api as client } from "@/api/client";

/* ────────────────────────────── Auth ────────────────────────────── */
export const auth = {
    login: (username, password) =>
        client.post("/api/auth/login", { username, password }).then((r) => r.data),
    logout: () =>
        client
            .post("/api/auth/logout")
            .then((r) => r.data)
            .catch(() => null),
    refresh: () => client.post("/api/auth/refresh").then((r) => r.data),
    me: () => client.get("/api/auth/me").then((r) => r.data),
    changePassword: (current_password, new_password) =>
        client
            .post("/api/auth/change-password", { current_password, new_password })
            .then((r) => r.data),
};

/* ────────────────────── Auth (cont.) ─────────────────────────────── */
auth.acceptInvite = (token, password) =>
    client.post("/api/auth/accept-invite", { token, password }).then((r) => r.data);
auth.inviteInfo = (token) =>
    client.get("/api/auth/invite-info", { params: { token } }).then((r) => r.data);

/* ────────────────────── Public marketing endpoints ──────────────── */
export const publicSite = {
    demos: () => client.get("/api/public/demos").then((r) => r.data),
    demoTrajectory: (run_id, max_samples = 8, decimate = "auto") =>
        client
            .get(`/api/public/demos/${run_id}/trajectory-data`, {
                params: { max_samples, decimate },
            })
            .then((r) => r.data),
    blockedCountries: () =>
        client.get("/api/access-requests/blocked-countries").then((r) => r.data),
    submitAccessRequest: (payload) =>
        client.post("/api/access-requests", payload).then((r) => r.data),
};

/* ────────────────────── Admin (superadmin only) ─────────────────── */
export const admin = {
    listAccessRequests: (status) =>
        client
            .get("/api/admin/access-requests", { params: status ? { status } : {} })
            .then((r) => r.data),
    capsuleConfig: () =>
        client.get("/api/admin/capsule-config").then((r) => r.data),
    approveAccessRequest: (request_id) =>
        client
            .post(`/api/admin/access-requests/${request_id}/approve`)
            .then((r) => r.data),
    rejectAccessRequest: (request_id, reason) =>
        client
            .post(`/api/admin/access-requests/${request_id}/reject`, { reason })
            .then((r) => r.data),
    listTenants: () => client.get("/api/admin/tenants").then((r) => r.data),
    updateSubscription: (tenant_id, patch) =>
        client.put(`/api/admin/tenants/${tenant_id}/subscription`, patch).then((r) => r.data),
    grantSubscription: (tenant_id, expires_in_days, notes) =>
        client
            .post(`/api/admin/tenants/${tenant_id}/subscription/grant`, null, {
                params: { expires_in_days, ...(notes ? { notes } : {}) },
            })
            .then((r) => r.data),
    listTrainingMirror: (params = {}) =>
        client.get("/api/admin/training-data", { params }).then((r) => r.data),
    trainingMirrorExportUrl: (start, end) => {
        const usp = new URLSearchParams();
        if (start) usp.set("start", start);
        if (end) usp.set("end", end);
        const base = process.env.REACT_APP_BACKEND_URL || "";
        return `${base}/api/admin/training-data/export.zip?${usp.toString()}`;
    },
    // Phase 6 — cross-cutting publish: lift any visible entity into
    // the cross-tenant preset library. The original row is preserved.
    // entity_type ∈ {scenarios, airframes, environments, engagements,
    //                 guidance-profiles, threats, terrain, obstacles}.
    publishToPresetLibrary: (entity_type, entity_id) =>
        client
            .post(`/api/admin/preset-library/${entity_type}/${entity_id}/publish`)
            .then((r) => r.data),
    listPresetLibraryEntityTypes: () =>
        client.get("/api/admin/preset-library/").then((r) => r.data),
};


export const users = {
    list: () => client.get("/api/users").then((r) => r.data),
    create: (payload) => client.post("/api/users", payload).then((r) => r.data),
    get: (username) => client.get(`/api/users/${username}`).then((r) => r.data),
    patch: (username, payload) =>
        client.patch(`/api/users/${username}`, payload).then((r) => r.data),
    resetPassword: (username, new_password) =>
        client
            .post(`/api/users/${username}/reset-password`, { new_password })
            .then((r) => r.data),
    remove: (username) => client.delete(`/api/users/${username}`).then((r) => r.data),
};

/* ────────────────────── Health (callable + helpers) ─────────────── */
//   `health()`           — original callable form, returns /api/health
//   `health.selfcheck()` — fresh selfcheck snapshot
export const health = (() => {
    const fn = () => client.get("/api/health").then((r) => r.data);
    fn.get = fn;
    fn.selfcheck = () => client.get("/api/health/selfcheck").then((r) => r.data);
    fn.backendDescribe = () => client.get("/api/backend/describe").then((r) => r.data);
    return fn;
})();

/* ────────────────────────────── Backend ─────────────────────────── */
export const backend = {
    describe: () => client.get("/api/backend/describe").then((r) => r.data),
};

/* ────────────────────────────── Presets ─────────────────────────── */
// Phase 8 — the listing payload changed from a family→list dict to a flat
// card array (one object per system preset + user preset visible to the
// caller). The card view never carries raw YAML / parsed dicts / file
// paths — admins use ``presets.raw(id)`` for the YAML body.
export const presets = {
    list: (params = {}) =>
        client.get("/api/presets", { params }).then((r) => r.data),
    get: (family, name) =>
        client.get(`/api/presets/${family}/${name}`).then((r) => r.data),
    raw: (id) =>
        client.get("/api/presets/raw", { params: { id } }).then((r) => r.data),
};

/* ────────────────────────── User presets ────────────────────────── */
export const userPresets = {
    create: (body) => client.post("/api/presets/user", body).then((r) => r.data),
    list: (params = {}) =>
        client.get("/api/presets/user", { params }).then((r) => r.data),
    get: (id) => client.get(`/api/presets/user/${id}`).then((r) => r.data),
    patch: (id, body) =>
        client.patch(`/api/presets/user/${id}`, body).then((r) => r.data),
    remove: (id) => client.delete(`/api/presets/user/${id}`).then((r) => r.data),
};

/* ────────────────────────────── Examples ────────────────────────── */
export const examples = {
    list: () => client.get("/api/examples/requests").then((r) => r.data),
    get: (name) => client.get(`/api/examples/requests/${name}`).then((r) => r.data),
};

/* ─────────────────────────── Demonstrations ─────────────────────── */
export const demos = {
    list: () => client.get("/api/demos").then((r) => r.data),
};

/* ────────────────── Requests (validate / compile) ──────────────── */
export const requests = {
    validate: (body) =>
        client.post("/api/requests/validate", body).then((r) => r.data),
    compile: (body) =>
        client.post("/api/requests/compile", body).then((r) => r.data),
};

/* ────────────────────────── Parameter sets ─────────────────────── */
export const parameterSets = {
    list: (params = {}) =>
        client.get("/api/parameter-sets", { params }).then((r) => r.data),
    get: (id) => client.get(`/api/parameter-sets/${id}`).then((r) => r.data),
    create: (body) => client.post("/api/parameter-sets", body).then((r) => r.data),
    update: (id, patch) =>
        client.put(`/api/parameter-sets/${id}`, patch).then((r) => r.data),
    remove: (id) => client.delete(`/api/parameter-sets/${id}`).then((r) => r.data),
    validate: (id) =>
        client.post(`/api/parameter-sets/${id}/validate`).then((r) => r.data),
    compile: (id) =>
        client.post(`/api/parameter-sets/${id}/compile`).then((r) => r.data),
};

/* ──────────────────────────────── Runs ───────────────────────────── */
export const runs = {
    submit: (body) => client.post("/api/runs", body).then((r) => r.data),
    create: (body) => client.post("/api/runs", body).then((r) => r.data),
    list: (params = {}) =>
        client.get("/api/runs", { params }).then((r) => r.data),
    get: (id) => client.get(`/api/runs/${id}`).then((r) => r.data),
    artifacts: (id) => client.get(`/api/runs/${id}/artifacts`).then((r) => r.data),
    log: (id, tail = 500) =>
        client
            .get(`/api/runs/${id}/log`, { params: { tail } })
            .then((r) => r.data),
    remove: (id) => client.delete(`/api/runs/${id}`).then((r) => r.data),
    cancel: (id) => client.post(`/api/runs/${id}/cancel`).then((r) => r.data),

    // Binary blob download. Caller is responsible for createObjectURL / revoke.
    artifactBlob: (id, name) =>
        client
            .get(`/api/runs/${id}/artifacts/${name}`, { responseType: "blob" })
            .then((r) => r.data),
    artifactText: (id, name) =>
        client
            .get(`/api/runs/${id}/artifacts/${name}`, { responseType: "text" })
            .then((r) => r.data),
    artifactJson: (id, name) =>
        client
            .get(`/api/runs/${id}/artifacts/${name}`, {
                responseType: "json",
                transformResponse: [
                    (data) => {
                        if (typeof data === "string") {
                            try {
                                return JSON.parse(data);
                            } catch (_e) {
                                return data;
                            }
                        }
                        return data;
                    },
                ],
            })
            .then((r) => r.data),
    artifactDownloadUrl: (id, name) => {
        const base = process.env.REACT_APP_BACKEND_URL || "";
        return `${base}/api/runs/${id}/artifacts/${encodeURIComponent(name)}`;
    },
    trajectoryData: (id, params = {}) =>
        client.get(`/api/runs/${id}/trajectory-data`, { params }).then((r) => r.data),
    // Phase 2 — replay + regression surface.
    compareTo: (a, b) =>
        client.post(`/api/runs/${a}/compare/${b}`).then((r) => r.data),
    regressionReport: (id) =>
        client.get(`/api/runs/${id}/regression-report`).then((r) => r.data),
    replay: (id, body = {}) =>
        client.post(`/api/runs/${id}/replay`, body).then((r) => r.data),
    replayInline: (id, body = {}) =>
        client.post(`/api/runs/${id}/replay-inline`, body).then((r) => r.data),
    // Phase 2 — Golden run promotion.
    promoteToGolden: (id, body = {}) =>
        client.post(`/api/runs/${id}/promote-to-golden`, body).then((r) => r.data),
    // Phase 3 — Report rendering.
    report: (id, format = "pdf") =>
        client
            .post(`/api/runs/${id}/report`, null, {
                params: { format },
                responseType: "blob",
            })
            .then((r) => r.data),
    // Phase 4 — reviewer workflow.
    submitForReview: (id) =>
        client.post(`/api/runs/${id}/submit-for-review`).then((r) => r.data),
    approve: (id, body = {}) =>
        client.post(`/api/runs/${id}/approve`, body).then((r) => r.data),
    reject: (id, body = {}) =>
        client.post(`/api/runs/${id}/reject`, body).then((r) => r.data),
    // Module 8 — reviewer state machine addendum.
    requestChanges: (id, body = {}) =>
        client.post(`/api/runs/${id}/request-changes`, body).then((r) => r.data),
    parameterProvenance: (id) =>
        client.get(`/api/runs/${id}/parameter-provenance`).then((r) => r.data),
    // Phase 7 — compare a run to an imported log.
    compareToLog: (id, body) =>
        client.post(`/api/runs/${id}/compare-to-log`, body).then((r) => r.data),
};

// ---------------------------------------------------------------------
// Phase 9 extensions — wire the rest of the 95-path surface into the
// operator console.
// ---------------------------------------------------------------------

/* ───────────────────────────── Jobs (Phase 5) ───────────────────── */
export const jobs = {
    list: (params = {}) => client.get("/api/jobs", { params }).then((r) => r.data),
    create: (body) => client.post("/api/jobs", body).then((r) => r.data),
    get: (id) => client.get(`/api/jobs/${id}`).then((r) => r.data),
    cancel: (id) => client.delete(`/api/jobs/${id}`).then((r) => r.data),
    children: (id) => client.get(`/api/jobs/${id}/children`).then((r) => r.data),
};

/* ───────────────────────── Golden runs (Phase 2) ────────────────── */
export const goldenRuns = {
    list: () => client.get("/api/golden-runs").then((r) => r.data),
};

/* ─────────────────────── Model registry (Phase 1) ───────────────── */
export const models = {
    list: (params = {}) =>
        client.get("/api/models", { params }).then((r) => r.data),
    create: (body) => client.post("/api/models", body).then((r) => r.data),
    get: (id) => client.get(`/api/models/${id}`).then((r) => r.data),
    patch: (id, body) => client.patch(`/api/models/${id}`, body).then((r) => r.data),
    getCard: (id) => client.get(`/api/models/${id}/card`).then((r) => r.data),
    putCard: (id, body) =>
        client.post(`/api/models/${id}/card`, body).then((r) => r.data),
    setLabel: (id, body) =>
        client.post(`/api/models/${id}/label`, body).then((r) => r.data),
    versionBump: (id, body) =>
        client.post(`/api/models/${id}/version-bump`, body).then((r) => r.data),
    versions: (id) =>
        client.get(`/api/models/${id}/versions`).then((r) => r.data),
};

/* ───────────────────── Calibration sessions (Phase 7) ───────────── */
export const calibration = {
    list: (params = {}) =>
        client.get("/api/calibration", { params }).then((r) => r.data),
    start: (body) => client.post("/api/calibration/start", body).then((r) => r.data),
    get: (id) => client.get(`/api/calibration/${id}`).then((r) => r.data),
    accept: (id, body = {}) =>
        client.post(`/api/calibration/${id}/accept`, body).then((r) => r.data),
    reject: (id, body = {}) =>
        client.post(`/api/calibration/${id}/reject`, body).then((r) => r.data),
};

/* ────────────────────── Log imports (Phase 7) ───────────────────── */
export const logs = {
    list: (params = {}) => client.get("/api/logs", { params }).then((r) => r.data),
    get: (id) => client.get(`/api/logs/${id}`).then((r) => r.data),
    remove: (id) => client.delete(`/api/logs/${id}`).then((r) => r.data),
    preview: (id, params = {}) =>
        client.get(`/api/logs/${id}/preview`, { params }).then((r) => r.data),
    upload: (formData) =>
        client
            .post("/api/logs/import", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            })
            .then((r) => r.data),
    rangeTruth: () =>
        client.get("/api/logs/range-truth-status").then((r) => r.data),
};

/* ───────────────────── Terrain + obstacles (Phase 6 + Module 4) ─── */
export const terrain = {
    list: (params = {}) =>
        client.get("/api/terrain", { params }).then((r) => r.data),
    get: (id) => client.get(`/api/terrain/${id}`).then((r) => r.data),
    remove: (id) => client.delete(`/api/terrain/${id}`).then((r) => r.data),
    upload: (formData) =>
        client
            .post("/api/terrain/upload", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            })
            .then((r) => r.data),
    fetchPublic: (body) =>
        client.post("/api/terrain/fetch-public", body).then((r) => r.data),
    elevation: (id, { lon, lat }) =>
        client
            .get(`/api/terrain/${id}/elevation`, { params: { lon, lat } })
            .then((r) => r.data),
    hillshadeUrl: (id) => `/api/terrain/${id}/hillshade.png`,
    // Phase 6 — metadata PATCH (DEM bytes immutable) + atomic clone.
    patch: (id, body) =>
        client.patch(`/api/terrain/${id}`, body).then((r) => r.data),
    clone: (id, body = undefined) =>
        client.post(`/api/terrain/${id}/clone`, body || {}).then((r) => r.data),
};

export const obstacles = {
    list: (params = {}) =>
        client.get("/api/obstacles", { params }).then((r) => r.data),
    get: (id) => client.get(`/api/obstacles/${id}`).then((r) => r.data),
    remove: (id) => client.delete(`/api/obstacles/${id}`).then((r) => r.data),
    upload: (formData) =>
        client
            .post("/api/obstacles/upload", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            })
            .then((r) => r.data),
    features: (id, bbox) =>
        client
            .get(`/api/obstacles/${id}/features`, { params: bbox })
            .then((r) => r.data),
    // Phase 6 — metadata PATCH (GeoJSON immutable) + atomic clone.
    patch: (id, body) =>
        client.patch(`/api/obstacles/${id}`, body).then((r) => r.data),
    clone: (id, body = undefined) =>
        client.post(`/api/obstacles/${id}/clone`, body || {}).then((r) => r.data),
};

/* ─────────────────────── Scenario attach + LOS (Module 4) ────────── */
export const scenarioTerrain = {
    attach: (scenarioId, body) =>
        client
            .post(`/api/sim/scenarios/${scenarioId}/terrain`, body)
            .then((r) => r.data),
    detach: (scenarioId) =>
        client.delete(`/api/sim/scenarios/${scenarioId}/terrain`).then((r) => r.data),
    attachObstacle: (scenarioId, obstacleId) =>
        client
            .post(`/api/sim/scenarios/${scenarioId}/obstacles`, { obstacle_id: obstacleId })
            .then((r) => r.data),
    detachObstacle: (scenarioId, obstacleId) =>
        client
            .delete(`/api/sim/scenarios/${scenarioId}/obstacles/${obstacleId}`)
            .then((r) => r.data),
    los: (scenarioId, body) =>
        client
            .post(`/api/sim/scenarios/${scenarioId}/los`, body)
            .then((r) => r.data),
    losGrid: (scenarioId, body) =>
        client
            .post(`/api/sim/scenarios/${scenarioId}/los/grid`, body)
            .then((r) => r.data),
};

/* ────────────────────────── Weather presets ─────────────────────── */
export const weather = {
    listPresets: () => client.get("/api/weather/presets").then((r) => r.data),
    getPreset: (name) =>
        client.get(`/api/weather/presets/${name}`).then((r) => r.data),
};

/* ────────────────────────── Review inbox ────────────────────────── */
export const review = {
    inbox: (params = {}) =>
        client.get("/api/review/inbox", { params }).then((r) => r.data),
};

/* ────────────────── Simulation Workbench (Module 1) ────────────── */
// Tenant-scoped scenario store + units preference + validation gate.
// See backend/routers/sim_scenarios.py and sim_preferences.py.
export const sim = {
    preferences: {
        get: () => client.get("/api/sim/preferences").then((r) => r.data),
        patch: (body) =>
            client.patch("/api/sim/preferences", body).then((r) => r.data),
    },
    scenarios: {
        list: (params = {}) =>
            client.get("/api/sim/scenarios", { params }).then((r) => r.data),
        create: (body) =>
            client.post("/api/sim/scenarios", body).then((r) => r.data),
        // Phase 20 CP3.0.c + CP3.a — Customer-demo archetype factory. Path
        // accepts one of {swarm, high_speed_ingress, mixed_raid,
        // degraded_environment}; optional body overrides the builder's
        // default ``name``. Returns the created SimScenario.
        createFromArchetype: (archetypeId, body = undefined) =>
            client
                .post(
                    `/api/sim/scenarios/from-archetype/${encodeURIComponent(archetypeId)}`,
                    body || {},
                )
                .then((r) => r.data),
        get: (id) => client.get(`/api/sim/scenarios/${id}`).then((r) => r.data),
        patch: (id, body) =>
            client.patch(`/api/sim/scenarios/${id}`, body).then((r) => r.data),
        remove: (id) =>
            client.delete(`/api/sim/scenarios/${id}`).then((r) => r.data),
        // Phase 6 — atomic one-shot clone replacing the client-side
        // create + attach* shim. Body is optional; recognised keys are
        // {name, description, tags}.
        clone: (id, body = undefined) =>
            client
                .post(`/api/sim/scenarios/${id}/clone`, body || {})
                .then((r) => r.data),
        validate: (id) =>
            client.get(`/api/sim/scenarios/${id}/validation`).then((r) => r.data),
        acknowledge: (id, body) =>
            client
                .post(`/api/sim/scenarios/${id}/acknowledgements`, body)
                .then((r) => r.data),
        // Module 2 — airframe attachment
        attachAirframe: (id, body) =>
            client.post(`/api/sim/scenarios/${id}/airframes`, body).then((r) => r.data),
        detachAirframe: (id, airframeId) =>
            client
                .delete(`/api/sim/scenarios/${id}/airframes/${airframeId}`)
                .then((r) => r.data),
        // Module 3 — environment attachment
        attachEnvironment: (id, body) =>
            client.post(`/api/sim/scenarios/${id}/environment`, body).then((r) => r.data),
        detachEnvironment: (id) =>
            client.delete(`/api/sim/scenarios/${id}/environment`).then((r) => r.data),
        // Module 5 — threat attachment (multi-attach, cap 5)
        attachThreat: (id, threat_id) =>
            client
                .post(`/api/sim/scenarios/${id}/threats`, { threat_id })
                .then((r) => r.data),
        detachThreat: (id, threat_id) =>
            client
                .delete(`/api/sim/scenarios/${id}/threats/${threat_id}`)
                .then((r) => r.data),
        // Module 6 — guidance profile attach + compare + harness
        attachGuidance: (id, guidance_profile_id) =>
            client
                .post(`/api/sim/scenarios/${id}/guidance-profile`,
                      { guidance_profile_id })
                .then((r) => r.data),
        detachGuidance: (id) =>
            client
                .delete(`/api/sim/scenarios/${id}/guidance-profile`)
                .then((r) => r.data),
        guidanceCompare: (id, body) =>
            client
                .post(`/api/sim/scenarios/${id}/guidance-compare`, body)
                .then((r) => r.data),
        diagnosticHarness: (id, params = {}) =>
            client
                .get(`/api/sim/scenarios/${id}/diagnostic-harness`, { params })
                .then((r) => r.data),
        // Module 7 — engagement attach + Pk curve
        attachEngagement: (id, engagement_id) =>
            client
                .post(`/api/sim/scenarios/${id}/engagement`, { engagement_id })
                .then((r) => r.data),
        detachEngagement: (id) =>
            client
                .delete(`/api/sim/scenarios/${id}/engagement`)
                .then((r) => r.data),
        pkCurve: (id, body) =>
            client
                .post(`/api/sim/scenarios/${id}/pk-curve`, body)
                .then((r) => r.data),
    },
    // Module 2 — airframe depth
    airframes: {
        list: (params = {}) =>
            client.get("/api/sim/airframes", { params }).then((r) => r.data),
        create: (body) =>
            client.post("/api/sim/airframes", body).then((r) => r.data),
        get: (id) =>
            client.get(`/api/sim/airframes/${id}`).then((r) => r.data),
        patch: (id, body) =>
            client.patch(`/api/sim/airframes/${id}`, body).then((r) => r.data),
        remove: (id) =>
            client.delete(`/api/sim/airframes/${id}`).then((r) => r.data),
        verify: (id) =>
            client.post(`/api/sim/airframes/${id}/verify`).then((r) => r.data),
        clone: (id, body = undefined) =>
            client
                .post(`/api/sim/airframes/${id}/clone`, body || {})
                .then((r) => r.data),
        exportXmlUrl: (id) => `/api/sim/airframes/${id}/export.xml`,
        importMultipart: async ({ file, coefficientsYaml, massProfileYaml, overrides = {} }) => {
            const fd = new FormData();
            fd.append("aircraft_xml", file);
            if (coefficientsYaml) fd.append("coefficients_yaml", coefficientsYaml);
            if (massProfileYaml) fd.append("mass_profile_yaml", massProfileYaml);
            for (const [k, v] of Object.entries(overrides)) {
                if (v != null) fd.append(k, v);
            }
            const r = await client.post("/api/sim/airframes/import", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            return r.data;
        },
        aeroTable: {
            upload: (id, body) =>
                client
                    .post(`/api/sim/airframes/${id}/aero-table`, body)
                    .then((r) => r.data),
            get: (id, coefficient) =>
                client
                    .get(`/api/sim/airframes/${id}/aero-table/${coefficient}`)
                    .then((r) => r.data),
        },
    },
    // Module 3 — environment depth
    environments: {
        list: (params = {}) =>
            client.get("/api/sim/environments", { params }).then((r) => r.data),
        create: (body) =>
            client.post("/api/sim/environments", body).then((r) => r.data),
        get: (id) => client.get(`/api/sim/environments/${id}`).then((r) => r.data),
        patch: (id, body) =>
            client.patch(`/api/sim/environments/${id}`, body).then((r) => r.data),
        remove: (id) =>
            client.delete(`/api/sim/environments/${id}`).then((r) => r.data),
        clone: (id, body = undefined) =>
            client
                .post(`/api/sim/environments/${id}/clone`, body || {})
                .then((r) => r.data),
        windProfile: (id, hMin = 0, hMax = 500, n = 64) =>
            client
                .get(`/api/sim/environments/${id}/wind-profile`, {
                    params: { h_min: hMin, h_max: hMax, n },
                })
                .then((r) => r.data),
        turbulencePsd: (id, { airspeed_mps = 50, f_min = 0.01, f_max = 10, n = 64 } = {}) =>
            client
                .get(`/api/sim/environments/${id}/turbulence-psd`, {
                    params: { airspeed_mps, f_min, f_max, n },
                })
                .then((r) => r.data),
        solar: (id, at) =>
            client
                .get(`/api/sim/environments/${id}/solar`, {
                    params: at ? { at } : {},
                })
                .then((r) => r.data),
    },
    validationRules: () =>
        client.get("/api/sim/validation-rules").then((r) => r.data),
    // Pass F-UI — FDM physics-airframe registry (untenanted reference
    // data). Drives the scenario-editor airframe picker + the
    // <AirframeAeroBadge> rendered next to each option.
    physicsAirframes: {
        list: () =>
            client.get("/api/sim/physics-airframes").then((r) => r.data),
    },
    // Module 5 — threat depth
    threats: {
        list: (params = {}) =>
            client.get("/api/sim/threats", { params }).then((r) => r.data),
        create: (body) =>
            client.post("/api/sim/threats", body).then((r) => r.data),
        get: (id) => client.get(`/api/sim/threats/${id}`).then((r) => r.data),
        patch: (id, body) =>
            client.patch(`/api/sim/threats/${id}`, body).then((r) => r.data),
        remove: (id) =>
            client.delete(`/api/sim/threats/${id}`).then((r) => r.data),
        clone: (id, body = undefined) =>
            client
                .post(`/api/sim/threats/${id}/clone`, body || {})
                .then((r) => r.data),
        preview: (id, { horizon_s = 60, dt_s = 0.25, scenario_id = null } = {}) => {
            const params = { horizon_s, dt_s };
            if (scenario_id) params.scenario_id = scenario_id;
            return client
                .get(`/api/sim/threats/${id}/preview`, { params })
                .then((r) => r.data);
        },
    },
    // Module 6 — guidance depth
    guidanceProfiles: {
        list: (params = {}) =>
            client.get("/api/sim/guidance-profiles", { params }).then((r) => r.data),
        create: (body) =>
            client.post("/api/sim/guidance-profiles", body).then((r) => r.data),
        get: (id) =>
            client.get(`/api/sim/guidance-profiles/${id}`).then((r) => r.data),
        patch: (id, body) =>
            client.patch(`/api/sim/guidance-profiles/${id}`, body).then((r) => r.data),
        remove: (id) =>
            client.delete(`/api/sim/guidance-profiles/${id}`).then((r) => r.data),
        clone: (id, body = undefined) =>
            client
                .post(`/api/sim/guidance-profiles/${id}/clone`, body || {})
                .then((r) => r.data),
    },
    // Module 7 — engagement & lethality depth
    engagements: {
        list: (params = {}) =>
            client.get("/api/sim/engagements", { params }).then((r) => r.data),
        create: (body) =>
            client.post("/api/sim/engagements", body).then((r) => r.data),
        get: (id) =>
            client.get(`/api/sim/engagements/${id}`).then((r) => r.data),
        patch: (id, body) =>
            client.patch(`/api/sim/engagements/${id}`, body).then((r) => r.data),
        remove: (id) =>
            client.delete(`/api/sim/engagements/${id}`).then((r) => r.data),
        clone: (id, body = undefined) =>
            client
                .post(`/api/sim/engagements/${id}/clone`, body || {})
                .then((r) => r.data),
    },
    runs: {
        linkSummary: (run_id) =>
            client
                .get(`/api/runs/${run_id}/link-summary`)
                .then((r) => r.data),
    },
    // Pass 3 — Quick Sim (non-expert relief valve)
    quickSim: {
        catalog: () =>
            client.get("/api/sim/quick-sim/catalog").then((r) => r.data),
        compose: (body) =>
            client.post("/api/sim/quick-sim/compose", body).then((r) => r.data),
    },
    // Pass 5 — Matrix / Sweep (analyst N×M kickoff)
    matrix: {
        composeAndRun: (body) =>
            client.post("/api/sim/matrix/compose-and-run", body).then((r) => r.data),
        get: (id) => client.get(`/api/sim/matrix/${id}`).then((r) => r.data),
        remove: (id) => client.delete(`/api/sim/matrix/${id}`).then((r) => r.data),
        exportCsvUrl: (id) => `/api/sim/matrix/${id}/export.csv`,
        exportPdfUrl: (id) => `/api/sim/matrix/${id}/export.pdf`,
    },
};
